/**
 * In-app documentation viewer (`/docs/:slug`) — REQUIRED per CLAUDE.md §In-App Help.
 *
 * **Admin gating is enforced in three places** because ADMIN_GUIDE.md carries sensitive
 * operational detail (infra config, backup procedures, security hardening) and treating it as
 * public is a security leak:
 *   1. here — a non-admin hitting `/docs/admin-guide` is redirected to the user guide
 *   2. the sidebar — the "Admin guide" item isn't rendered at all for non-admins
 *   3. the doc-switcher pills below — `admin-guide` is filtered out for non-admins
 *
 * Markdown is rendered by a small purpose-built parser rather than a dependency, and — critically
 * — raw HTML in the source is **escaped, never injected**. Everything reaches the DOM as React
 * text nodes or elements we construct, so there is no `dangerouslySetInnerHTML` anywhere and no
 * XSS surface even though these files are authored content.
 */

import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { Alert } from '@/components/common/Alert';
import { useAuth } from '@/contexts/AuthContext';
import { renderMarkdown, type Heading } from '@/utils/markdown';

const DOC_MAP: Record<string, { file: string; title: string; adminOnly: boolean }> = {
  'user-guide': { file: 'USER_GUIDE.md', title: 'User guide', adminOnly: false },
  'admin-guide': { file: 'ADMIN_GUIDE.md', title: 'Admin guide', adminOnly: true },
};

export function DocsViewer() {
  const { slug = 'user-guide' } = useParams<{ slug: string }>();
  const { isAdmin } = useAuth();

  const [source, setSource] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const doc = DOC_MAP[slug];

  useEffect(() => {
    if (!doc) return;
    // Gate 1: never even fetch the admin guide without the admin capability.
    if (doc.adminOnly && !isAdmin) return;

    setSource(null);
    setError(null);

    fetch(`/docs/${doc.file}`)
      .then((response) => {
        if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
        return response.text();
      })
      .then(setSource)
      .catch((cause: Error) => setError(cause.message));
  }, [doc, isAdmin, slug]);

  if (!doc) {
    return <Navigate to="/docs/user-guide" replace />;
  }

  // Gate 1 (redirect): a non-admin never renders the admin guide.
  if (doc.adminOnly && !isAdmin) {
    return <Navigate to="/docs/user-guide" replace />;
  }

  // Gate 3: the switcher pills hide admin-only docs from non-admins.
  const availableDocs = Object.entries(DOC_MAP).filter(
    ([, meta]) => !meta.adminOnly || isAdmin,
  );

  return (
    <div className="lg:flex lg:gap-8">
      <article className="min-w-0 flex-1">
        <nav className="mb-6 flex flex-wrap gap-2" aria-label="Documentation">
          {availableDocs.map(([docSlug, meta]) => (
            <Link
              key={docSlug}
              to={`/docs/${docSlug}`}
              className={[
                'rounded-full px-3 py-1 text-sm font-medium transition-colors',
                docSlug === slug
                  ? 'bg-primary-500 text-white'
                  : 'bg-surface-100 text-surface-700 hover:bg-surface-200 dark:bg-surface-800 dark:text-surface-200 dark:hover:bg-surface-700',
              ].join(' ')}
            >
              {meta.title}
            </Link>
          ))}
        </nav>

        {error ? (
          <Alert tone="error" title="Could not load this document">
            {error}. The guides ship as static files in <code>frontend/public/docs/</code>.
          </Alert>
        ) : source === null ? (
          <p className="text-sm text-surface-500 dark:text-surface-400">Loading…</p>
        ) : (
          <DocContent source={source} />
        )}
      </article>
    </div>
  );
}

function DocContent({ source }: { source: string }) {
  const { blocks, headings } = useMemo(() => renderMarkdown(source), [source]);

  return (
    <div className="lg:flex lg:flex-row-reverse lg:gap-8">
      <TableOfContents headings={headings} />
      <div className="min-w-0 flex-1 space-y-4">{blocks}</div>
    </div>
  );
}

function TableOfContents({ headings }: { headings: Heading[] }) {
  // h2–h4 only, per the spec.
  const entries = headings.filter((heading) => heading.level >= 2 && heading.level <= 4);
  if (entries.length === 0) return null;

  return (
    <nav
      aria-label="On this page"
      className="mb-6 lg:mb-0 lg:sticky lg:top-6 lg:h-fit lg:w-64 lg:shrink-0"
    >
      <details className="lg:open" open>
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-surface-500 dark:text-surface-400 lg:cursor-default lg:list-none">
          On this page
        </summary>
        <ul className="mt-2 space-y-1 border-l border-surface-200 dark:border-surface-700">
          {entries.map((heading) => (
            <li key={heading.id}>
              <a
                href={`#${heading.id}`}
                className="block border-l-2 border-transparent py-0.5 pl-3 text-sm text-surface-600 transition-colors hover:border-primary-500 hover:text-primary-600 dark:text-surface-400 dark:hover:text-primary-400"
                style={{ paddingLeft: `${(heading.level - 2) * 0.75 + 0.75}rem` }}
              >
                {heading.text}
              </a>
            </li>
          ))}
        </ul>
      </details>
    </nav>
  );
}

/** Fenced code block with a copy button. */
export function CodeBlock({ code, language }: { code: string; language?: string }): ReactNode {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    void navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="relative group">
      <pre className="overflow-x-auto rounded-lg bg-surface-900 p-4 text-xs text-surface-100 dark:bg-surface-950">
        <code>{code}</code>
      </pre>
      <button
        type="button"
        onClick={copy}
        aria-label="Copy code"
        className="absolute right-2 top-2 rounded-md bg-surface-700/80 p-1.5 text-surface-200 opacity-0 transition-opacity hover:bg-surface-600 group-hover:opacity-100 focus:opacity-100"
      >
        {copied ? (
          <span className="px-1 text-xs">Copied</span>
        ) : (
          <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
      {language ? <span className="sr-only">Language: {language}</span> : null}
    </div>
  );
}
